<template>
  <div
    :id="customId || null"
    class="fpui-input-card-radio"
  >
    <fpui-input-label
      v-if="label"
      :required="required"
      :helper="helper"
    >
      {{ label }}
    </fpui-input-label>
    <div
      class="fpui-input-card-radio-grid"
      :class="`fpui-input-card-radio-cols-${columns}`"
      role="radiogroup"
      :aria-label="ariaLabel || label || null"
      :aria-disabled="disabled ? 'true' : null"
    >
      <div
        v-for="option in options"
        :key="option.value"
        class="fpui-input-card-radio-option"
        :class="[optionClass, { selected: option.value === value, disabled: isDisabled(option) }]"
        role="radio"
        :tabindex="option.value === focusValue ? 0 : -1"
        :aria-checked="option.value === value ? 'true' : 'false'"
        :aria-disabled="isDisabled(option) ? 'true' : null"
        :data-testid="testId ? `${testId}-${String(option.value).toLowerCase()}` : null"
        @click="select(option)"
        @keydown="onKeydown($event, option)"
      >
        <div
          v-if="option.image || option.icon"
          class="fpui-input-card-radio-media"
        >
          <img
            v-if="option.image"
            :src="option.image"
            alt=""
          >
          <i
            v-else
            :class="option.icon"
            aria-hidden="true"
          />
        </div>
        <div class="fpui-input-card-radio-main">
          <div
            v-if="option.category"
            class="fpui-input-card-radio-category"
          >
            <span
              class="fpui-input-card-radio-dot"
              :class="`accent-${option.accent || 'blue'}`"
            />
            {{ option.category }}
          </div>
          <div class="fpui-input-card-radio-heading">
            <span class="fpui-input-card-radio-title">{{ option.title }}</span>
            <span
              v-if="option.badge"
              class="fpui-input-card-radio-badge"
            >{{ option.badge }}</span>
          </div>
          <div
            v-if="option.description"
            class="fpui-input-card-radio-description"
            v-tooltip="clampDescription ? option.description : null"
            :class="{ clamped: clampDescription }"
          >
            {{ option.description }}
          </div>
          <ul
            v-if="option.features && option.features.length"
            class="fpui-input-card-radio-features"
          >
            <li
              v-for="feature in option.features"
              :key="feature"
            >
              <i
                class="fp4 fp4-check-bold"
                aria-hidden="true"
              />
              <span>{{ feature }}</span>
            </li>
          </ul>
          <div
            v-if="isDisabled(option) && option.disabledReason"
            class="fpui-input-card-radio-reason"
          >
            {{ option.disabledReason }}
          </div>
          <template v-if="option.footer">
            <div class="fpui-input-card-radio-spacer" />
            <div class="fpui-input-card-radio-footer">
              <span class="fpui-input-card-radio-footer-label">{{ option.footer.label }}</span>
              {{ option.footer.text }}
            </div>
          </template>
        </div>
        <div
          v-if="$slots.action"
          class="fpui-input-card-radio-action"
        >
          <slot
            name="action"
            :option="option"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// A radio group drawn as cards: each choice gets room for a description,
// a list of features and a footer. Emits the value of the picked card.
// Keyboard: Tab reaches the group once, arrows move the choice, Enter and Space pick.
export default {
  name: 'FpuiInputCardRadio',
  props: {
    value: { type: [String, Number], default: null },
    // { value, title, description, category, accent, features, footer: { label, text },
    //   badge, image, icon, disabled, disabledReason }
    options: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    helper: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    columns: { type: Number, default: 3 },
    // Cut the description after two lines; the full text stays in its tooltip.
    clampDescription: { type: Boolean, default: false },
    ariaLabel: { type: String, default: '' },
    customId: { type: String, default: '' },
    // Each card gets data-testid="<testId>-<value in lower case>".
    testId: { type: String, default: '' },
    optionClass: { type: String, default: '' }
  },
  emits: ['input'],
  computed: {
    enabledValues () {
      return this.options.filter(option => !this.isDisabled(option)).map(option => option.value)
    },
    // The one card Tab lands on: the picked one, else the first that can be picked.
    focusValue () {
      return this.enabledValues.includes(this.value) ? this.value : this.enabledValues[0]
    }
  },
  methods: {
    isDisabled (option) {
      return this.disabled || !!option.disabled
    },
    select (option) {
      if (this.isDisabled(option) || option.value === this.value) return
      this.$emit('input', option.value)
    },
    onKeydown (event, option) {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key]
      if (step) {
        event.preventDefault()
        this.move(option, step)
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.select(option)
      }
    },
    // Picks the next card that can be picked, wrapping around, and moves the focus to it.
    move (option, step) {
      const values = this.enabledValues
      if (!values.length) return
      const from = values.indexOf(option.value)
      const next = values[(from + step + values.length) % values.length]
      this.select(this.options.find(o => o.value === next))
      const index = this.options.findIndex(o => o.value === next)
      this.$el.querySelectorAll('[role="radio"]')[index]?.focus()
    }
  }
}
</script>

<style lang="less" scoped>
.fpui-input-card-radio-grid {
  display: grid;
  gap: 16px;

  each(range(4), {
    &.fpui-input-card-radio-cols-@{value} {
      grid-template-columns: repeat(@value, minmax(0, 1fr));
    }
  });

  @media @mobile {
    &[class*="fpui-input-card-radio-cols-"] {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

.fpui-input-card-radio-option {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 16px;
  background: @white;
  border: 2px solid @grey_dark;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: @grey;
  }

  &:focus-visible {
    outline: 2px solid @blue_flash;
    outline-offset: 2px;
  }

  &.selected {
    border-color: @blue_flash;
    box-shadow: 0 0 0 3px @blueLight;
  }

  .input-button-disabled();

  &.disabled:hover {
    border-color: @grey_dark;
  }
}

.fpui-input-card-radio-media {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 6px;
  background: @background-color_aside;
  color: @grey;
  font-size: 18px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.fpui-input-card-radio-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.fpui-input-card-radio-category {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: @label-color;
}

.fpui-input-card-radio-dot {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;

  &.accent-blue { background: @blue; }
  &.accent-green { background: @green; }
  &.accent-orange { background: @orange; }
}

.fpui-input-card-radio-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.fpui-input-card-radio-title {
  font-size: 16px;
  font-weight: 600;
  color: @text-color;
  overflow-wrap: anywhere;
}

.fpui-input-card-radio-badge {
  padding: 1px 8px;
  border-radius: 10px;
  background: @blueLight;
  color: @blue;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.fpui-input-card-radio-description {
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: @label-color;

  &.clamped {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.fpui-input-card-radio-features {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 14px;
    color: @text-color;
  }

  i {
    flex-shrink: 0;
    font-size: 12px;
    color: @green;
  }
}

.fpui-input-card-radio-reason {
  margin-top: 4px;
  font-size: 12px;
  color: @grey;
}

// Pushes the footer to the bottom, so footers line up across a row of cards.
.fpui-input-card-radio-spacer {
  flex: 1;
  min-height: 12px;
}

.fpui-input-card-radio-footer {
  padding-top: 12px;
  border-top: 1px solid @grey_dark;
  font-size: 12px;
  line-height: 1.5;
  color: @grey;
}

.fpui-input-card-radio-footer-label {
  font-weight: 600;
  color: @label-color;
}

.fpui-input-card-radio-action {
  flex-shrink: 0;
}
</style>
